import { pluralise } from "utils/pluralise";

// User-visible message strings — toasts, validation errors, and inline error
// states. Organised by the page or component that raises them.
// Dynamic messages (those needing runtime values) are exported as functions.

// ── Common messages ───────────────────────────────────────────────────────────
// Generic, caller-agnostic messages. Check here before defining a new constant
// — if the wording would be identical or near-identical, reuse from this section.

export const GENERIC_ERROR_MESSAGE =
  "An error occurred. Please try again later.";
export const DATA_BACKEND_ERROR_MESSAGE =
  "Error fetching data. There may be an issue with the database or backend services.";
export const DATA_FETCH_ERROR_MESSAGE = "Error while fetching data";
export const VALUE_REQUIRED_MESSAGE = "Required";
export const VALUE_AT_LEAST_MESSAGE = (min: number) =>
  `Must be ${min} or greater`;
export const VALUE_GREATER_THAN_MESSAGE = (min: number) =>
  `Must be greater than ${min}`;

// ── HTTP interceptor (AxiosInstance) ─────────────────────────────────────────

export const LICENCE_EXPIRED_API_ERROR_MESSAGE =
  "Your licence has expired or is inactive. Please contact support.";
export const INVALID_REQUEST_API_ERROR_MESSAGE =
  "Invalid request. Please check your data and try again.";
export const INVALID_REQUEST_RESOURCE_API_ERROR_MESSAGE = (resource: string) =>
  `Invalid ${resource} request. Please check your data and try again.`;
export const FORBIDDEN_API_ERROR_MESSAGE =
  "You do not have permission to perform this action.";
export const FORBIDDEN_RESOURCE_API_ERROR_MESSAGE = (resource: string) =>
  `You do not have permission to access this ${resource}.`;
export const CONFLICT_API_ERROR_MESSAGE =
  "A conflict occurred. The record may have been modified elsewhere.";
export const CONFLICT_RESOURCE_API_ERROR_MESSAGE = (resource: string) =>
  `A conflict occurred saving this ${resource}. It may have been modified elsewhere.`;
export const NOT_FOUND_API_ERROR_MESSAGE =
  "The requested resource could not be found.";
export const NOT_FOUND_RESOURCE_API_ERROR_MESSAGE = (resource: string) =>
  `This ${resource} could not be found.`;

// ── Workspace initial load (FarmsPage) ───────────────────────────────────────

export const WORKSPACE_LOAD_FATAL_ERROR_MESSAGE =
  "Failed to load workspace data. Please refresh the page or log in again.";
export const FARMS_LOAD_ERROR_MESSAGE =
  "Farm data failed to load. Some features may be unavailable.";
export const ZONES_LOAD_ERROR_MESSAGE =
  "Zone data failed to load. Some features may be unavailable.";
export const INPUTS_LOAD_ERROR_MESSAGE =
  "Input data failed to load. Some features may be unavailable.";
export const UNITS_LOAD_ERROR_MESSAGE =
  "Unit data failed to load. Some features may be unavailable.";

// ── Profile page ──────────────────────────────────────────────────────────────

export const PROFILE_LOAD_ERROR_MESSAGE =
  "Failed to load profile data. Please try again.";
export const PROFILE_SAVE_SUCCESS_MESSAGE = "Profile saved successfully.";
export const PROFILE_SUPPORT_SYSTEM_NOTICE =
  "Your profile details will be stored in our support system. If you use other Back Paddock products, any changes made here may be reflected elsewhere.";
export const PROFILE_SAVE_ERROR_MESSAGE =
  "Failed to update profile. Please try again.";

export const PROFILE_EMAIL_REQUIRED_MESSAGE = "Email is required";
export const PROFILE_FIRST_NAME_REQUIRED_MESSAGE = "First name is required";
export const PROFILE_FIRST_NAME_PATTERN_MESSAGE =
  "First name can only contain letters, spaces, and hyphens";
export const PROFILE_LAST_NAME_REQUIRED_MESSAGE = "Last name is required";
export const PROFILE_LAST_NAME_PATTERN_MESSAGE =
  "Last name can only contain letters, spaces, and hyphens";
export const PROFILE_PREFERRED_NAME_PATTERN_MESSAGE =
  "Preferred name can only contain letters, spaces, and hyphens";
export const PROFILE_MOBILE_PHONE_REQUIRED_MESSAGE = "Mobile phone is required";
export const PROFILE_MOBILE_PHONE_PATTERN_MESSAGE =
  "Please enter a valid Australian mobile number (e.g., +61412345678 or 0412345678)";
export const PROFILE_ALTERNATE_PHONE_PATTERN_MESSAGE =
  "Please enter a valid phone number.";
export const PROFILE_ADDRESS1_REQUIRED_MESSAGE = "Address line 1 is required";
export const PROFILE_CITY_REQUIRED_MESSAGE = "City is required";
export const PROFILE_STATE_REQUIRED_MESSAGE = "State is required";
export const PROFILE_POSTCODE_REQUIRED_MESSAGE = "Postcode is required";
export const PROFILE_POSTCODE_PATTERN_MESSAGE =
  "Please enter a valid 4-digit Australian postcode";
export const PROFILE_ROLE_REQUIRED_MESSAGE = "Role is required";
export const PROFILE_SEGMENTS_REQUIRED_MESSAGE =
  "At least one segment is required";
export const PROFILE_SEGMENTS_MIN_MESSAGE =
  "Please select at least one segment";
export const PROFILE_UNSAVED_CONFIRM_MESSAGE =
  "Your unsaved changes will be lost. Are you sure you want to leave this page?";

// ── Global Catalogue — TableGrid / GridRightPanel ─────────────────────────────

export const GC_COLLECTION_UPDATED_SUCCESS_MESSAGE =
  "Collection updated successfully";
export const GC_COLLECTION_DISABLED_SUCCESS_MESSAGE =
  "Collection disabled successfully";
export const GC_DOCUMENT_CREATED_SUCCESS_MESSAGE =
  "Document created successfully";
export const GC_DOCUMENT_UPDATE_GUARD_ERROR_MESSAGE =
  "Error updating document. Please try again.";

// ── Global Catalogue — organisation mutation ──────────────────────────────────

export const GC_ORG_BACKEND_ERROR_MESSAGE =
  "Error fetching organisation data. There may be an issue with the database or backend services.";
export const GC_ORG_FETCH_ERROR_MESSAGE =
  "Error while fetching organisation data";

// ── Global Catalogue — reference collection mutation hooks ────────────────────

export const GC_ACTION_MODE_FETCH_ERROR_MESSAGE =
  "Error while fetching Action Mode collection data";
export const GC_AG_SECTOR_FETCH_ERROR_MESSAGE =
  "Error while fetching AgSector collection data";
export const GC_CHEMICAL_GROUP_FETCH_ERROR_MESSAGE =
  "Error while fetching ChemicalGroup collection data";
export const GC_GENUS_SPECIES_FETCH_ERROR_MESSAGE =
  "Error while fetching GenusSpecies Collection data";
export const GC_MAIN_END_USE_FETCH_ERROR_MESSAGE =
  "Error while fetching Main End Use collection data";
export const GC_MANUFACTURER_FETCH_ERROR_MESSAGE =
  "Error while fetching Manufacturer collection data";
export const GC_MOA_GROUP_FETCH_ERROR_MESSAGE =
  "Error while fetching Moa Group collection data";
export const GC_PARAMETER_FETCH_ERROR_MESSAGE =
  "Error while fetching Parameter collection data";
export const GC_PHYSICAL_STATE_FETCH_ERROR_MESSAGE =
  "Error while fetching Physical State collection data";
export const GC_PRODUCT_GRADE_FETCH_ERROR_MESSAGE =
  "Error while fetching Product Grade collection data";
export const GC_PRODUCT_GROUP_FETCH_ERROR_MESSAGE =
  "Error while fetching Product Group collection data";
export const GC_SEGMENT_FETCH_ERROR_MESSAGE =
  "Error while fetching Segment collection data";
export const GC_SPECIES_TYPE_FETCH_ERROR_MESSAGE =
  "Error while fetching Species Type collection data";
export const GC_TECH_STACK_FETCH_ERROR_MESSAGE =
  "Error while fetching Tech Stack collection data";

// ── Global Catalogue — unit / lookup mutation hooks ───────────────────────────

export const GC_SUPPLIER_NAMES_FETCH_ERROR_MESSAGE =
  "Error while fetching Active Supplier Names";
export const GC_SEED_SPECIES_FETCH_ERROR_MESSAGE =
  "Error while fetching Seed Species Crops data";

// ── Global Catalogue — collection search / read ───────────────────────────────

export const GC_SEARCH_FAILED_MESSAGE = (err: string): string =>
  `Search failed: ${err}`;
export const GC_FETCH_TOTAL_PAGES_FAILED_MESSAGE = (error: unknown): string =>
  `Failed to fetch total pages: ${error}`;

// ── Map canvas (PaddockMapCanvas) ─────────────────────────────────────────────

export const MAP_HOLE_POINT_OUTSIDE_BOUNDARY_MESSAGE =
  "Points must be inside the paddock boundary.";
export const MAP_HOLE_SEGMENT_CROSSES_HOLE_MESSAGE =
  "Line segment must not cross an existing hole.";
export const MAP_SPLIT_CUT_INVALID_MESSAGE =
  "Cut line must cross the paddock from one side to the other.";
export const MAP_BOUNDARY_SELF_INTERSECTS_MESSAGE =
  "Invalid boundary — the polygon edges cross each other. Please redraw.";
export const MAP_BOUNDARY_MIN_POINTS_MESSAGE =
  "A boundary requires at least 3 points.";
export const MAP_VERTEX_DELETE_MIN_VERTICES_MESSAGE =
  "Cannot delete — ring must retain at least 3 vertices.";
export const MAP_HOLE_OUTSIDE_BOUNDARY_MESSAGE = (i: number): string =>
  `Hole ${i + 1} extends outside the paddock boundary.`;
export const MAP_HOLES_OVERLAP_MESSAGE = (i: number, j: number): string =>
  `Hole ${i + 1} and hole ${j + 1} overlap — holes must not intersect.`;
export const MAP_SPLIT_NO_VALID_CUTS_MESSAGE =
  "Could not split: no valid cut lines.";
export const MAP_CUTOUT_MIN_POINTS_MESSAGE =
  "A cutout requires at least 3 points.";
export const MAP_CUTOUT_SELF_INTERSECTS_MESSAGE =
  "Invalid cutout — the polygon edges cross each other. Please redraw.";
export const MAP_CUTOUT_OUTSIDE_BOUNDARY_MESSAGE =
  "Cutout must be entirely within the paddock boundary.";

// ── Tasks workspace ───────────────────────────────────────────────────────────

export const TARGET_SPECIES_DUPLICATES_SKIPPED_MESSAGE = (
  count: number
): string =>
  `${count} duplicate ${count === 1 ? "target" : "targets"} already added – skipped.`;
export const TASK_SAVE_ERROR_MESSAGE = "Error saving task. Please try again.";
export const TASK_DELETE_ERROR_MESSAGE =
  "Error deleting task. Please try again.";
// Short enough to sit beside the Task column label (OP-2534).
export const EMPTY_TASK_REMOVED_MESSAGE = "Empty task removed";
export const INPUT_DOCUMENT_UPDATE_ERROR_MESSAGE =
  "Error updating document. Please try again.";

// ── Create plan modal ─────────────────────────────────────────────────────────

export const CREATE_PLAN_ERROR_MESSAGE =
  "Unable to create paddock plan. Please try again.";

// ── Network status ────────────────────────────────────────────────────────────

export const NETWORK_OFFLINE_BANNER_MESSAGE =
  "No network connection detected. Please check your connection.";

// ── Reset password page ───────────────────────────────────────────────────────

export const RESET_PASSWORD_SUCCESS_MESSAGE = "Your password has been reset.";

// ── Plan preview — Insights and Farm diagnostics sections (OP-2490) ─────────
// On screen the adviser is told how to fill an empty section; the printed
// copy goes to the grower, so it carries a neutral line instead.
export const PLAN_PREVIEW_DIAGNOSTICS_GHOST_MESSAGE =
  "Not enough data yet — add rainfall and yield in Farm diagnostics → Yearly overview, then reopen this preview.";
export const PLAN_PREVIEW_PRINT_NO_HISTORY_MESSAGE = "Not enough history";
export const PLAN_PREVIEW_ROTATION_GHOST_MESSAGE =
  "Not enough data yet — add rainfall and management practice in Farm diagnostics → Rotation efficiency, then reopen this preview.";
export const PLAN_PREVIEW_DIAGNOSTICS_NO_PADDOCK_MESSAGE =
  "This plan is not linked to a paddock, so it has no paddock history.";
export const PLAN_PREVIEW_NO_NUTRIENTS_MESSAGE =
  "No nutrients planned — add nutrient values to this plan's soil inputs to see a tally.";
export const PLAN_PREVIEW_NO_NUTRIENTS_APPLIED_MESSAGE =
  "No nutrients applied yet — record soil inputs with nutrient values in this plan's actual tasks to see a tally.";
export const PLAN_PREVIEW_NO_ACTUALS_MESSAGE =
  "No actuals recorded for this plan yet — record completed tasks to see them here.";
export const PLAN_PREVIEW_NO_SECTIONS_MESSAGE =
  "Every paddock section is turned off — use What to include to show them.";

// ── Live totals — Nutrient balance and Resistance (OP-2563) ─────────────────

export const NUTRIENT_BALANCE_NO_REMOVAL_MESSAGE =
  "No crop removal yet — add an expected yield to the production task, and check the crop has nutrient removal values.";
export const NUTRIENT_BALANCE_PARTIAL_REMOVAL_MESSAGE = (planCount: number) =>
  `Removal leaves out ${planCount} plan${planCount === 1 ? "" : "s"} with no expected yield or crop removal values — add them to the production task to include ${planCount === 1 ? "it" : "them"}.`;
export const RESISTANCE_MISSING_SOURCE_MESSAGE = (
  planCount: number,
  sourceLabel: string
) =>
  `${planCount} earlier plan${planCount === 1 ? " has" : "s have"} no ${sourceLabel} applications recorded, so ${planCount === 1 ? "it isn't" : "they aren't"} counted.`;
export const RESISTANCE_EMPTY_MESSAGE =
  "No mode of action groups yet — add a herbicide, insecticide or fungicide to a task to see how often each group is used.";

// ── Planning lenses (OP-2549) — why a lens is greyed out ─────────────────────

export const PLANNING_LENS_NEEDS_PADDOCK_MESSAGE =
  "Select a paddock or plan in the farm tree to open its plan.";
export const PLANNING_LENS_NEEDS_SCOPE_MESSAGE =
  "Select a paddock, a farm or your organisation in the farm tree to use this view.";

// ── Insights — Margin & break-even crop average (OP-2451) ────────────────────

export const CROP_AVERAGE_NO_OTHER_PLANS_MESSAGE = (
  cropName: string,
  year: number,
  isActual: boolean
) =>
  isActual
    ? `No other ${cropName} ${year} plans with Actual data yet.`
    : `No other ${cropName} ${year} plans in your organisation yet.`;
export const CROP_AVERAGE_TOO_FEW_PLANS_MESSAGE = (
  cropName: string,
  year: number,
  planCount: number,
  minPlans: number
) =>
  `Only ${planCount} other ${cropName} ${year} plan${planCount === 1 ? "" : "s"} to compare with — the average needs at least ${minPlans}.`;
export const CROP_AVERAGE_NO_PLAN_MARGIN_MESSAGE =
  "No margin for this plan yet — add yield, price and costs to compare it with the average.";

// ── Insights — Nutrition lens (OP-2508) ──────────────────────────────────────

export const NUTRITION_INVALID_SCOPE_MESSAGE =
  "Select your organisation, a farm or a paddock in the farm tree to see the nutrient audit.";
export const NUTRITION_NO_PLANS_MESSAGE =
  "No plans in these years — create a plan for this paddock, or choose more years.";
export const NUTRITION_NO_PADDOCK_DATA_MESSAGE =
  "No nutrient data yet — add fertilisers with a nutrient analysis, and a harvest yield, to see the balance.";
export const NUTRITION_CHART_NO_DATA_MESSAGE =
  "Add fertilisers with a nutrient analysis and a harvest yield to fill this chart.";
export const NUTRITION_NO_NUTRIENTS_SELECTED_MESSAGE =
  "Choose at least one nutrient to show.";
// Nutrition tooltips are a short heading plus short lines (TipContent), so
// they read at a glance rather than as one long sentence.
export const NUTRITION_MISSING_REMOVAL_MESSAGE = (seasons: number[]) => ({
  title: `Crop removal not counted for ${seasons.join(", ")}`,
  lines: [
    "There's no harvest yield, or the crop has no grain nutrient content.",
    "Add a yield to the plan's Production task to include it.",
  ],
});
export const NUTRITION_NO_REMOVAL_KEY_MESSAGE =
  "There's no crop removal figure for this nutrient, so only the amount applied is shown.";
export const NUTRITION_BALANCE_HELP = {
  title: "How the balance is worked out",
  lines: [
    "Applied: each fertiliser's nutrient analysis × its rate.",
    "Removed: harvest yield × the grain's nutrient content. In Plan, it uses the yield chosen under Yield. Stubble isn't counted.",
    "Balance: applied − removed, over the years shown.",
    "▲ Building above zero. ▼ Declining below zero.",
  ],
};
export const NUTRITION_RATE_LOCKED_OPEN_PLAN_MESSAGE = {
  title: "Open in Paddock plan",
  lines: [
    "Change this rate in Paddock plan, or select the farm in the tree to change it here.",
  ],
};
export const NUTRITION_RATE_LOCKED_UNSAVED_MESSAGE = {
  title: "Unsaved changes in Paddock plan",
  lines: ["Save or discard them there first."],
};
export const NUTRITION_RATE_LOCKED_AMBIGUOUS_STEPS_MESSAGE = {
  title: "Duplicate entries",
  lines: [
    "This application has duplicate entries. Change its rates in Paddock plan.",
  ],
};
export const NUTRITION_RATE_EDIT_HINT =
  "Click a rate to change it. Changes are saved to the plan straight away, and can be undone.";
export const NUTRITION_PROGRAM_EDIT_HINT =
  "Click a rate to change it, or add and remove products. Changes are saved to the plan straight away, and can be undone.";
export const NUTRITION_RATE_REQUIRED_MESSAGE = "Enter a rate of 0 or more.";
export const NUTRITION_TARGET_REQUIRED_MESSAGE =
  "Choose which application or sowing it goes on.";
export const NUTRITION_UNDO_DONE_MESSAGE = "Undone.";
export const NUTRITION_SEASON_NO_PLAN_MESSAGE =
  "No plan this year. Create one from the farm tree to set its nutrition program.";
export const NUTRITION_OVER_AREA_MESSAGE = (applied: string, plan: string) => ({
  title: `${applied} ha is more than the plan's ${plan} ha`,
  lines: [
    "Its nutrients are overstated.",
    "Check the product's area, or the plan's, in Planning.",
  ],
});
export const NUTRITION_NOT_SAVED_MESSAGE = (
  failed: { description: string; reason: string }[]
) =>
  `${failed.length === 1 ? "1 change" : `${failed.length} changes`} not saved: ${failed
    .map((f) => `${f.description} (${f.reason})`)
    .join("; ")}. Click Retry to try again.`;
export const NUTRITION_FARM_TOTAL_HELP = {
  title: "Area-weighted total",
  lines: [
    "Each paddock's balance weighted by its area, so larger paddocks count for more.",
  ],
};

// ── Insights — Rotation planner lens (OP-2501) ───────────────────────────────

export const ROTATION_INVALID_SCOPE_MESSAGE =
  "Select your organisation, a farm or a paddock in the farm tree to see the rotation.";
export const ROTATION_NO_PADDOCKS_MESSAGE =
  "No paddocks here yet — add paddocks to this farm to plan its rotation.";
export const ROTATION_GET_STARTED_MESSAGE =
  "No plans in these years yet. Select a paddock’s year and press Enter to set its crop — saving creates the plan.";
export const ROTATION_PAST_YEARS_HELP =
  "Yield and sowing for earlier years. This year and later always show Plan. Crop and variety come from the plan either way, and editing always sets the plan.";
// "How to use" popover — each shortcut with what it does.
export const ROTATION_HELP_ITEMS: { keys: string[]; text: string }[] = [
  { keys: ["Click", "Arrows"], text: "Select a paddock and year" },
  {
    keys: ["Enter", "Double-click"],
    text: "Set the crop, variety and sowing. Saving creates or updates that paddock’s plan for the year.",
  },
  { keys: ["Ctrl/⌘+C"], text: "Copy the selected crop, variety and sowing" },
  {
    keys: ["Shift+click", "Ctrl/⌘+click"],
    text: "Select a range of paddocks and years, or add them one at a time",
  },
  {
    keys: ["Ctrl/⌘+V"],
    text: "Paste onto the selected paddocks and years — you check the changes before they’re saved",
  },
  { keys: ["Delete"], text: "Delete the selected plans — you confirm first" },
  { keys: ["Right-click"], text: "Set crop, copy, paste or delete" },
  { keys: ["Esc"], text: "Clear the copy and the selection" },
];
export const ROTATION_HELP_FOOTNOTE =
  "Past years can be edited too, to record history. Actual sowing and yields are recorded in Planning.";
export const ROTATION_NO_ZONE_MESSAGE = {
  title: "No zone linked",
  lines: [
    "This paddock can’t have a plan until a zone is linked to it.",
    "Link one in the map view.",
  ],
};
export const ROTATION_LOCKED_OPEN_PLAN_MESSAGE = {
  title: "Open in Planning",
  lines: [
    "Change this plan in Planning, or select the farm in the tree to change it here.",
  ],
};
export const ROTATION_LOCKED_UNSAVED_MESSAGE = {
  title: "Unsaved changes in Planning",
  lines: ["Save or discard them there first, then change it here."],
};
export const ROTATION_MULTIPLE_SOWING_MESSAGE = {
  title: "Multiple sowing records",
  lines: [
    "This plan has more than one crop establishment task, switch to Planning to edit.",
    "You can still change the crop and variety here.",
  ],
};
/** The sowing record's crop and variety, e.g. "Barley · Spartacus". */
const sownLabel = (crop: string, variety: string | null) =>
  [crop, variety].filter(Boolean).join(" · ");
/** How a plan sows: one seed, a seed mix in one task, or several tasks. */
export type RotationSowingKind = "single" | "mix" | "multiple";
export const ROTATION_SOWING_MISMATCH_TIP = (
  crop: string,
  variety: string | null,
  kind: RotationSowingKind
) => ({
  title: "Sowing doesn’t match the crop",
  lines: [
    kind === "mix"
      ? `A seed in the mix is for ${sownLabel(crop, variety)}.`
      : `A sowing record is for ${sownLabel(crop, variety)}.`,
    {
      single:
        "Saving here sets it to the plan’s crop and variety, or change it in Planning.",
      mix: "Fine for a blend; otherwise change it in Planning.",
      multiple:
        "This plan has more than one crop establishment task — check them in Planning.",
    }[kind],
  ],
});
export const ROTATION_SOWING_MISMATCH_EDITOR_MESSAGE = (
  crop: string,
  variety: string | null,
  kind: RotationSowingKind
) =>
  ({
    single: `The sowing record is for ${sownLabel(crop, variety)}. Saving sets it to the crop and variety above.`,
    mix: `A seed in the mix is for ${sownLabel(crop, variety)}. Fine for a blend; otherwise change it in Planning.`,
    multiple: `A sowing record is for ${sownLabel(crop, variety)}. Check this plan’s crop establishment tasks in Planning.`,
  })[kind];
export const ROTATION_SEED_MIX_EDIT_NOTE =
  "Saving keeps the mix as it is. Switch to Planning to change its seeds.";
export const ROTATION_SAVE_CREATES_PLAN_MESSAGE =
  "Saving creates a plan for this paddock and year. It starts on the sowing date, or 1 January without one.";
export const ROTATION_SAVE_UPDATES_PLAN_MESSAGE =
  "Saving updates this plan. Its other tasks and actuals aren’t changed.";
export const ROTATION_SOWING_TASK_MESSAGE =
  "Sowing details are saved as the plan’s crop establishment task.";
export const ROTATION_CROP_REQUIRED_MESSAGE = "Choose a crop.";
export const ROTATION_SOWING_DATE_HELP =
  "Also the plan’s start date, unless it was set in Planning.";
export const ROTATION_SOWING_DEFAULTS_NOTE =
  "Rate, unit and seed cost are filled in from the crop’s defaults. Change them if they’re different for this paddock.";
export const ROTATION_CROP_LOCKED_ACTUAL_MESSAGE =
  "This plan has an Actual sowing record, so its crop and variety are changed in Planning. You can still set its planned sowing here.";
export const ROTATION_RATE_UNIT_REQUIRED_MESSAGE =
  "Choose a unit for the sowing rate.";
export const ROTATION_SOWING_DATE_OUT_OF_SEASON_MESSAGE = (season: number) =>
  `Choose a sowing date in ${season}.`;
export const ROTATION_SAVE_ERROR_MESSAGE =
  "This plan wasn’t saved. Check your connection and try again.";
export const ROTATION_ZONE_LOAD_ERROR_MESSAGE =
  "This paddock’s zone couldn’t be loaded, so the plan wasn’t created. Try again.";
export const ROTATION_PASTE_NOTHING_TO_COPY_MESSAGE =
  "There’s no crop here to copy. Select a paddock and year with a crop, then press Ctrl/⌘+C.";
export const ROTATION_PASTE_NO_TARGETS_MESSAGE =
  "Select where to paste first — Ctrl/⌘+click adds a paddock and year, Shift+click selects a range.";
export const ROTATION_PASTE_TOO_MANY_MESSAGE = (max: number, count: number) =>
  `You can paste onto up to ${max} plans at a time, and ${count} are selected. Select fewer and paste again.`;
export const ROTATION_PASTE_SKIP_NO_ZONE =
  "No zone linked — link one in the map view";
export const ROTATION_PASTE_SKIP_LOCKED =
  "Open or unsaved in Planning — save or close it there first";
export const ROTATION_PASTE_SKIP_SECOND_ROW =
  "A second crop row — add second crops in Planning";
export const ROTATION_COPY_NOT_BLOCK_MESSAGE =
  "Copy one paddock and year, or a block of them — not separate ones picked with Ctrl/⌘+click.";
export const ROTATION_PASTE_OUTSIDE_MESSAGE = (count: number) =>
  `${count === 1 ? "1 crop doesn’t" : `${count} crops don’t`} fit in the paddocks and years shown, so ${count === 1 ? "it isn’t" : "they aren’t"} pasted.`;
export const ROTATION_PASTE_SKIP_ACTUAL_SOWING =
  "Has an Actual sowing record — change its crop in Planning";
export const ROTATION_PASTE_SKIP_MULTIPLE_SOWING =
  "Has more than one crop establishment task — switch to Planning to edit";
export const ROTATION_PASTE_OTHER_TASKS_MESSAGE = (count: number) =>
  `${count === 1 ? "1 plan has" : `${count} plans have`} other tasks, which stay as they are — check them in Planning if the crop changes.`;
export const ROTATION_PASTE_DATE_SHIFT_MESSAGE =
  "The sowing date moves to the same day in each year.";
export const ROTATION_BULK_BUSY_MESSAGE =
  "Wait for these plans to finish saving, then try again.";
export const ROTATION_PASTE_REPLACES_MIX_MESSAGE = (count: number) =>
  `${count === 1 ? "1 plan sows" : `${count} plans sow`} a seed mix, which the paste replaces with the copied seed.`;
export const ROTATION_PASTE_CLEARS_SOWING_MESSAGE =
  "The copied crop has no sowing details, so any on these plans are cleared.";
export const ROTATION_PASTE_FAILED_MESSAGE = (count: number) =>
  `${count === 1 ? "1 plan wasn’t" : `${count} plans weren’t`} saved. Retry, or point to or tab to the warning on each to see why.`;
export const ROTATION_ENTERPRISE_EMPTY_MESSAGE =
  "Set crops for this year to see the enterprise mix.";
export const ROTATION_SEED_EMPTY_MESSAGE =
  "Add sowing rates to see how much seed is needed.";
export const ROTATION_SEED_MISSING_MESSAGE = (count: number, area: string) =>
  `${count === 1 ? "1 plan" : `${count} plans`} (${area} ha) ${count === 1 ? "has" : "have"} no sowing rate, so ${count === 1 ? "it isn’t" : "they aren’t"} in these totals. Add a rate to include ${count === 1 ? "it" : "them"}:`;
export const ROTATION_SEED_HELP = {
  title: "How seed is worked out",
  lines: [
    "Sowing rate × area for each sowing record, the same way as Live totals.",
    "Grouped by the crop and variety being sown, so a mix counts each seed in it.",
  ],
};
export const ROTATION_DELETE_BODY =
  "Each plan is deleted with all its tasks. This can’t be undone from here.";
export const ROTATION_DELETE_OTHER_TASKS_MESSAGE = (count: number) =>
  `${count === 1 ? "1 plan has" : `${count} plans have`} tasks besides sowing — they’re deleted too.`;
export const ROTATION_DELETE_ACTUALS_MESSAGE = (count: number) =>
  `${count === 1 ? "1 plan has" : `${count} plans have`} Actual records — they’re deleted too.`;
export const ROTATION_DELETE_LOCKED_MESSAGE = (count: number) =>
  `${count === 1 ? "1 plan is" : `${count} plans are`} open or unsaved in Planning, so ${count === 1 ? "it isn’t" : "they aren’t"} deleted.`;
export const ROTATION_DELETE_TOO_MANY_MESSAGE = (max: number, count: number) =>
  `You can delete up to ${max} plans at a time, and ${count} are selected. Select fewer and try again.`;
export const ROTATION_DELETE_NOTHING_MESSAGE =
  "There’s no plan here to delete.";
export const ROTATION_DELETE_FAILED_MESSAGE = (count: number) =>
  `${count === 1 ? "1 plan wasn’t" : `${count} plans weren’t`} deleted. Retry, or point to or tab to the warning on each to see why.`;
export const ROTATION_DELETE_ERROR_MESSAGE =
  "This plan wasn’t deleted. Check your connection and try again.";
export const ROTATION_PLAN_GONE_MESSAGE =
  "This plan has changed or been removed. Close this and try again.";

// ── LimeMate bulk processing (OP-2491) ───────────────────────────────────────

export const LIME_MATE_BULK_FILE_TYPE_ERROR_MESSAGE =
  "Only .csv files can be uploaded. Export your lab results as CSV and try again.";
export const LIME_MATE_BULK_FILE_TOO_LARGE_ERROR_MESSAGE = (maxMb: number) =>
  `This file is larger than ${maxMb} MB. Split it into smaller files and upload each one.`;
export const LIME_MATE_BULK_FILE_EMPTY_ERROR_MESSAGE =
  "This file has no data rows. Check it has a header row and at least one sample.";
export const LIME_MATE_BULK_FILE_TOO_MANY_ROWS_ERROR_MESSAGE = (
  rows: number,
  maxRows: number
) =>
  `This file has ${rows} rows — the limit is ${maxRows}. Split it into smaller files and upload each one.`;
export const LIME_MATE_BULK_FILE_READ_ERROR_MESSAGE =
  "This file could not be read. Check it is a CSV file and try again.";
export const LIME_MATE_BULK_UNMATCHED_FIELDS_MESSAGE = (fields: string[]) =>
  `Match these required fields to continue: ${fields.join(", ")}.`;
export const LIME_MATE_BULK_NO_VALID_SAMPLES_MESSAGE =
  "No samples are valid. Fix the errors below in your CSV, or go back and check the column matches and settings.";
export const LIME_MATE_BULK_RESULTS_NOT_SAVED_MESSAGE =
  "Results aren't saved. Check your CSV has downloaded before leaving this page.";
export const LIME_MATE_BULK_BETA_NOTICE_MESSAGE =
  "Bulk processing is in beta. Validation ranges are still being confirmed, so check results before relying on them.";
// Row validation — shown against a row and field in the review table.
export const LIME_MATE_BULK_SAMPLE_ID_MISSING_MESSAGE =
  "Sample ID is missing. Add one so the row can be grouped into a sample.";
export const LIME_MATE_BULK_SAMPLE_CONFLICT_MESSAGE = (existing: string) =>
  `Conflicts with "${existing}" on another row of this sample. Use one value for the whole sample.`;
export const LIME_MATE_BULK_DUPLICATE_SAMPLE_ID_MESSAGE = (line: number) =>
  `Sample ID is already used on row ${line}. Give each sample its own ID.`;
export const LIME_MATE_BULK_VALUE_MISSING_MESSAGE =
  "Value is missing. Add it to the CSV or check the column match.";
export const LIME_MATE_BULK_VALUE_NOT_NUMBER_MESSAGE = (value: string) =>
  `"${value}" is not a number. Values such as "<0.1" can't be used — enter a number.`;
export const LIME_MATE_BULK_VALUE_OUT_OF_RANGE_MESSAGE = (
  min: number,
  max: number
) => `Must be between ${min} and ${max}. Check the value and its units.`;
export const LIME_MATE_BULK_VALUE_NEGATIVE_MESSAGE = "Must be 0 or more.";
export const LIME_MATE_BULK_CALCULATED_ECEC_OUT_OF_RANGE_MESSAGE = (
  min: number,
  max: number
) =>
  `Calculated eCEC must be between ${min} and ${max}. Check the cation values and their units.`;
export const LIME_MATE_BULK_DEPTH_RANGE_INVALID_MESSAGE = (value: string) =>
  `"${value}" is not a depth range. Use the form 0–10.`;
export const LIME_MATE_BULK_DEPTH_TO_NOT_GREATER_MESSAGE =
  "Must be greater than depth from.";
export const LIME_MATE_BULK_TEXTURE_UNRECOGNISED_MESSAGE = (value: string) =>
  `"${value}" is not a recognised texture class, e.g. "Clay Loam".`;
export const LIME_MATE_BULK_SAMPLE_NO_DEPTHS_MESSAGE =
  "Sample has no depth values. Add at least one depth.";
export const LIME_MATE_BULK_SAMPLE_TOO_MANY_DEPTHS_MESSAGE = (
  count: number,
  max: number
) =>
  `Sample has ${count} depths — the maximum is ${max}. Remove the extra depths.`;
export const LIME_MATE_BULK_DEPTHS_OVERLAP_MESSAGE = (
  first: string,
  second: string
) =>
  `${first} cm overlaps ${second} cm. Fix the depth ranges so they don't overlap.`;

// ── SodiCalc (OP-2585) ───────────────────────────────────────────────────

// Calculator inputs — shown against the field, and no results until fixed.
export const SODI_CALC_EC_INVALID_MESSAGE = "Enter an EC of 0 dS/m or more.";
export const SODI_CALC_ECEC_INVALID_MESSAGE =
  "Enter an eCEC greater than 0 cmol(+)/kg.";
export const SODI_CALC_ESP_INVALID_MESSAGE = "Enter an ESP from 0 to 100%.";
export const SODI_CALC_TARGET_ESP_INVALID_MESSAGE =
  "Enter a target ESP of 0% or more.";
export const SODI_CALC_DEPTH_INVALID_MESSAGE =
  "Enter a depth greater than 0 cm.";
export const SODI_CALC_BD_INVALID_MESSAGE =
  "Enter a bulk density greater than 0 g/cm³.";
export const SODI_CALC_PURITY_INVALID_MESSAGE =
  "Enter a purity greater than 0% and up to 100%.";
export const SODI_CALC_COST_INVALID_MESSAGE = "Enter a cost of $0/t or more.";
export const SODI_CALC_FIX_INPUTS_MESSAGE =
  "Fix the highlighted inputs to see results.";
// Bulk processing.
export const SODI_CALC_BULK_NO_VALID_SAMPLES_MESSAGE =
  "No samples are ready. Fix the highlighted values in the grid, or go back and check the column matches.";
export const SODI_CALC_BULK_SAMPLES_NEED_FIXING_MESSAGE = (count: number) =>
  `${pluralise(count, "sample")} ${count === 1 ? "needs" : "need"} fixing. ${count === 1 ? "It's" : "They're"} left out of the download until the highlighted values are fixed.`;
export const SODI_CALC_BULK_SAMPLE_ID_MISSING_MESSAGE =
  "Sample ID is missing. Add one to the CSV.";
export const SODI_CALC_BULK_DUPLICATE_SAMPLE_ID_MESSAGE = (line: number) =>
  `Sample ID is already used on row ${line}. Give each sample its own ID.`;
export const SODI_CALC_BULK_ESP_UNAVAILABLE_MESSAGE =
  "ESP can't be worked out. Type an ESP here, or add sodium and eCEC for this sample to the CSV.";
export const SODI_CALC_BULK_VALUE_MISSING_MESSAGE =
  "Missing. Type a value here, or go back and check the column match.";
export const SODI_CALC_BULK_FIX_TITLE = "To fix";
export const SODI_CALC_BULK_NOT_A_NUMBER_MESSAGE =
  "That isn't a number, so the value wasn't changed. Enter a number.";

// Tasks grid — View options (OP-2556)
export const TASKS_GRID_NEW_LAYOUT_HELP =
  "Shows the inputs as one spreadsheet you can shape below. Switch off to go back to the original layout.";
export const ORG_DEFAULT_WATER_RATE_HELP =
  "The organisation's default, for everyone in it. Spray tasks take it when their first liquid product is added. Changing it doesn't change existing tasks.";
export const ORG_DEFAULT_WATER_RATE_SAVED = "Saved for the organisation";
export const ORG_DEFAULT_WATER_RATE_SAVE_ERROR =
  "Couldn't save the default water rate. Check your connection and try again.";

// Update defaults (OP-2499)
export const UPDATE_DEFAULTS_SAVED = "Defaults updated";
export const UPDATE_DEFAULTS_NOTHING_TO_UPDATE = "No changes to update";
export const UPDATE_DEFAULTS_FAILED = "Defaults not saved. Try again.";
export const UPDATE_DEFAULTS_NOT_SAVED_ERROR =
  "The server did not save the default.";

// Planning — undo (OP-2539)
export const PLANNING_UNDONE_MESSAGE = (change: string) => `Undone: ${change}.`;
export const PLANNING_REDONE_MESSAGE = (change: string) => `Redone: ${change}.`;

// Planning — plan crop vs crop establishment (OP-2536)
export const PLAN_CROP_MISMATCH_MESSAGE = (
  seedCrop: string,
  planCrop: string
) =>
  `Crop establishment is sown with ${seedCrop}, but the plan's crop is ${planCrop}.`;
export const PLAN_CROP_MISMATCH_ACTUAL_MESSAGE = (
  seedCrop: string,
  planCrop: string
) =>
  `Actual crop establishment was sown with ${seedCrop}, but the plan's crop is ${planCrop}.`;
export const PLAN_CROP_MISMATCH_SEVERAL_HINT =
  "To change the sowing instead, update the crop establishment tasks below.";

// Planning — leaving a plan with changes that can't be saved
export const PLAN_CROP_REQUIRED_MESSAGE = "Crop type is required.";
export const PLAN_START_DATE_REQUIRED_MESSAGE = "Start date is required.";
export const PLAN_AREA_REQUIRED_MESSAGE = "Area is required.";
export const UNSAVED_CHANGES_CANT_SAVE_MESSAGE = (reason: string) =>
  `These changes can't be saved yet. ${reason} Keep editing to fix this, or discard the changes.`;

// Planning — Paddocks lens (OP-2481)
export const PADDOCKS_NO_FARMS_MESSAGE =
  "No farms yet. Add a farm, then add its paddocks.";
export const FARM_NAME_REQUIRED_MESSAGE = "Farm name is required.";
export const PADDOCK_NAME_REQUIRED_MESSAGE = "Paddock name is required.";
export const FARM_SAVE_ERROR_MESSAGE =
  "Unable to save the farm. Check your connection and try again.";
export const PADDOCK_SAVE_ERROR_MESSAGE =
  "Unable to save the paddock. Check your connection and try again.";
export const PADDOCKS_LOAD_ERROR_MESSAGE =
  "Couldn't load your farms and paddocks. Refresh the page to try again.";
export const PADDOCKS_NO_MATCHES_MESSAGE =
  "No farms or paddocks match. Change the search or show all.";
export const PADDOCKS_DELETE_FARMS_NOTE =
  "Deleting a farm also deletes all its paddocks.";
export const PADDOCKS_DELETE_PLANS_WARNING = (count: number) =>
  `${count} ${count === 1 ? "plan" : "plans"} on these paddocks will no longer show.`;
export const PADDOCKS_EXCLUDED_MESSAGE = (count: number) =>
  `${pluralise(count, "row")} excluded`;
export const PADDOCKS_INCLUDED_MESSAGE = (count: number) =>
  `${pluralise(count, "row")} included`;
export const PADDOCKS_DELETED_MESSAGE = (count: number) =>
  `${pluralise(count, "row")} deleted`;
export const PADDOCKS_INCLUDE_SKIPPED_MESSAGE = (count: number) =>
  `${count} ${count === 1 ? "paddock stays" : "paddocks stay"} excluded while ${count === 1 ? "its farm is" : "their farms are"} excluded. Include the farm instead.`;
export const PADDOCKS_BULK_ERROR_MESSAGE = (count: number) =>
  `${count} couldn't be saved. Check your connection and try again.`;

// ── Copy data between paddocks (OP-2519) ────────────────────────────────────

export const COPY_TOOL_NO_SOURCE_MESSAGE =
  "Choose a document to copy from to get started.";
export const COPY_TOOL_NO_DESTINATION_MESSAGE = "Now choose where to copy to.";
export const COPY_TOOL_NO_TASKS_MESSAGE = (mode: string) =>
  `The source has no ${mode} tasks to copy. Choose another document or switch to the other data.`;
export const COPY_TOOL_NO_TASKS_CROP_ONLY_MESSAGE = (mode: string) =>
  `The source has no ${mode} tasks. Switch to Add to or Add as new to copy only its crop and variety.`;
export const COPY_TOOL_SELECTION_SUMMARY_MESSAGE = (
  taskCount: number,
  stepCount: number,
  documentCount: number
) =>
  `${pluralise(taskCount, "task")} (${pluralise(stepCount, "step")}) → ${pluralise(documentCount, "document")}`;
export const COPY_TOOL_AREA_UNKNOWN_MESSAGE = (count: number) =>
  `${pluralise(count, "document")} without a known area. Set the plan's area, or the paddock zone's area in the map view`;
export const COPY_TOOL_AREA_LOAD_ERROR_MESSAGE =
  "Some plan areas couldn't be loaded. Close the copy tool and try again";
export const COPY_TOOL_AREA_SUMMARY_MESSAGE = (area: string, unit: string) =>
  `${area} ${unit} in total`;
export const COPY_TOOL_NO_PADDOCKS_MESSAGE =
  "No paddocks yet. Add farms and paddocks to this organisation first.";
export const COPY_TOOL_LOAD_ERROR_MESSAGE =
  "We couldn't load your paddocks. Close the copy tool and try again.";
export const COPY_TOOL_UNSAVED_CHANGES_WARNING =
  "The open plan has unsaved changes. Copy saves them first.";
export const COPY_TOOL_OPEN_PLAN_SAVE_ERROR_MESSAGE = (reason: string) =>
  `The open plan couldn't be saved, so nothing was copied. ${reason}`.trim();
export const COPY_TOOL_OPEN_PLAN_SAVE_TIMEOUT_REASON =
  "Planning is still saving. Check your connection, then copy again.";
export const COPY_TOOL_COPY_RUNNING_REASON =
  "A copy is still running. Copy again once it finishes.";
export const COPY_TOOL_UNKNOWN_PADDOCK_NAME = "an unknown paddock";
export const COPY_TOOL_COPY_ERROR_MESSAGE =
  "Unable to copy. Check your connection and try again.";
export const COPY_TOOL_COPY_PENDING_MESSAGE = (
  copiedCount: number,
  totalCount: number
) => `Copied ${copiedCount} of ${pluralise(totalCount, "document")}...`;
export const COPY_TOOL_COPY_SUCCESS_MESSAGE = (documentCount: number) =>
  `Copied to ${pluralise(documentCount, "document")}.`;
export const COPY_TOOL_COPY_PARTIAL_ERROR_MESSAGE = (
  copiedCount: number,
  totalCount: number,
  failedNames: string
) =>
  `Copied to ${copiedCount} of ${pluralise(totalCount, "document")}. Couldn't copy to ${failedNames}. Try those again.`;
export const COPY_TOOL_COPY_DOCUMENT_LABEL = (
  documentName: string,
  paddockName: string
) => `${documentName} (${paddockName})`;
export const COPY_TOOL_COPY_NEW_DOCUMENT_LABEL = (paddockName: string) =>
  `${paddockName} (new document)`;

// ── Copy data between paddocks — data filter (OP-2520) ──────────────────────

export const COPY_TOOL_FILTER_NO_SOURCE_MESSAGE =
  "Tick a Source document to choose what to copy.";
export const COPY_TOOL_FILTER_EMPTY_MESSAGE = (mode: string) =>
  `The source has no ${mode} data to filter.`;
export const COPY_TOOL_TASKS_NO_SOURCE_MESSAGE =
  "Tick a Source document to see the tasks to copy.";
export const COPY_TOOL_TASKS_EMPTY_MESSAGE = (mode: string) =>
  `The source has no ${mode} tasks.`;
export const COPY_TOOL_FILTER_UNCATEGORISED_MESSAGE = (mode: string) =>
  `The source's ${mode} tasks have no categorised steps, so it's copied whole.`;
export const COPY_TOOL_NOTHING_SELECTED_MESSAGE =
  "Nothing to copy. Tick a category or task.";
export const COPY_TOOL_CROP_ONLY_SUMMARY_MESSAGE = (documentCount: number) =>
  `Crop & variety → ${pluralise(documentCount, "document")}`;
export const COPY_TOOL_REPLACE_PARTIAL_TOOLTIP =
  "Replace copies the whole plan, so choosing it ticks every category and task again. To copy part of a plan, use Add to or Add as new.";
export const COPY_TOOL_REPLACE_CONFIRM_TITLE = "Replace existing data?";
export const COPY_TOOL_REPLACE_CONFIRM_DESCRIPTION = (
  documentCount: number,
  mode: string
) =>
  `Replaces the ${mode} data in ${pluralise(documentCount, "existing document")}, including crop and variety. This can't be undone.`;
export const COPY_TOOL_SWITCHED_TO_ADD_TO_NOTE =
  "Switched to Add to, as Replace copies the whole plan.";
export const COPY_TOOL_MATCH_RULE_CHANGE_TITLE = "Change the copy rule?";
export const COPY_TOOL_TASK_NO_STEPS_REASON =
  "No steps, so only copied with the whole plan";
export const COPY_TOOL_TASK_NO_CATEGORISED_STEPS_REASON =
  "No categorised steps, so only copied with the whole plan";
export const COPY_TOOL_TASK_NO_MATCH_REASON =
  "No steps in the ticked categories, so it isn't copied";
export const COPY_TOOL_MATCH_RULE_CHANGE_DESCRIPTION =
  "Your task ticks will be reset to match the ticked categories.";

// Add a missing record from a picker (OP-2498)
export const ADD_RECORD_ROW_LABEL = (text: string) => `Add "${text}"`;
export const ADD_RECORD_SAVE_FAILED =
  "Not saved. Check your connection and try again.";
export const ADD_QUICK_RECORD_QUESTION = (name: string, noun: string) =>
  `Add "${name}" as a ${noun}?`;
